import { Box, Checkbox, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, styled, tableCellClasses } from '@mui/material'
import Pagination from '../../../../components/TablePagination'
import React, { useEffect, useRef } from 'react'
import { visuallyHidden } from '@mui/utils'
import { type AdvancedFilterPostRequest, type ConjuctionOptions, type SelectAssetList } from '../../types'
import { getTemplateModal } from './utils/getTemplateModal'
import { type RootState } from '../../../../store'
import { type ConnectedProps, connect } from 'react-redux'
import { advancedAssignedAssetCreateFilter, fetchAssetListForCreateGroup } from '../../redux/actionCreator'
import { type AssetsFoCreateGroupState } from '../../redux/assetForCreateGroupSlice'
import { type Option } from '../../../../components/GenXDropdown'
import AuthError from '../../../../components/ErrorComponents/AuthError'
import LanguageStrings from '../../../../i18n/locales'
import SearchBox from '../../../../components/SearchBox'
import { type AdvFilterParameterConditions } from '../GroupDetailsComponents/AssignedAssetList'
import { Button } from '../../../../components/Button'
import AdvancedFilter from '../../../../components/AdvancedFilterComponents/AdvancedFilter'
import dayjs from 'dayjs'
import Clearfilter from '../../../../assets/Clearfilter.svg'
import _ from 'lodash'
import CheckboxDropdown, { type CheckboxDropdownMode } from '../../../../components/CheckboxDropdown'

const SelectAssetsStrings = LanguageStrings().AssetGroupStrings.CreateGroupComponent.SelectAssets

type Order = 'asc' | 'desc'

interface Sort {
  order: Order
  orderBy: string
}

export interface Condition {
  key: string
  operation: string
  value: string | string[]
}

const style = {
  width: '99%',
  bgcolor: '#313030',
  border: '1px solid #101010',
  borderRadius: '10px',
  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
  marginBottom: '16px',
  padding: 0
}

interface EnhancedTableProps {
  dataList: SelectAssetList[]
  order: Order
  orderBy: string
  onRequestSort: (event: React.MouseEvent<unknown>, newOrderBy: keyof SelectAssetList) => void
  selectedOption: CheckboxDropdownMode
  setSelectedOption: React.Dispatch<React.SetStateAction<CheckboxDropdownMode>>
  handleSelectionModeChange: (prevMode: CheckboxDropdownMode, newMode: CheckboxDropdownMode) => void
  currPageAssets: string[]
  currPageSelectedAssets: string[]
  containerRef: React.RefObject<HTMLDivElement>
}

function EnhancedTableHead (props: EnhancedTableProps): any {
  const { order, orderBy, onRequestSort, dataList, selectedOption, setSelectedOption, handleSelectionModeChange, currPageSelectedAssets, currPageAssets, containerRef } = props
  const createSortHandler =
        (newOrderBy: keyof SelectAssetList) => (event: React.MouseEvent<unknown>) => {
          onRequestSort(event, newOrderBy)
        }
  if (dataList !== null && dataList !== undefined) {
    return (
      <TableHead data-testid='table-header'>
        <TableRow>
          <StyledTableCell padding="checkbox"style={{ borderRight: 'none', paddingLeft: '8px' }}>
            <CheckboxDropdown containerRef={containerRef} onSelectionModeChange={handleSelectionModeChange} selectedOption={selectedOption} setSelectedOption={setSelectedOption}
                            currPageTotal={currPageAssets.length} currPageSelectedAssets={currPageSelectedAssets} />
          </StyledTableCell>
          {headCells.map((headCell: HeadCell, idx: number) => (
              <StyledTableCell
                  key={headCell.id}
                  align='left'
                  padding={headCell.disablePadding ? 'none' : 'normal'}
                  sortDirection={orderBy === headCell.id ? order : false}
                  style={{ borderLeft: idx === 0 ? 'none' : 'inherit' }}
              >
                <TableSortLabel
                    hideSortIcon={!headCell.sortable}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={headCell.sortable ? createSortHandler(headCell.id) : undefined}
                    aria-label={headCell.label}
                    data-testid="test-sort"
                >
                    {headCell.label}
                    {orderBy === headCell.id
                      ? (
                        <Box component="span" sx={visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                        )
                      : null}
                </TableSortLabel>
              </StyledTableCell>
          ))}
        </TableRow>
      </TableHead>
    )
  }
}

interface HeadCell {
  disablePadding: boolean
  id: any
  label: string
  numeric: boolean
  sortable: boolean
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: '#F0F0F0',
    fontWeight: '700',
    fontSize: 12,
    border: '2px solid #202020',
    padding: '5px 16px',
    fontFamily: 'Honeywell Sans Web',
    lineHeight: '20px'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: '#d0d0d0',
    paddingLeft: '17px',
    borderBottom: '1px solid #202020',
    borderRight: 0,
    fontFamily: 'Honeywell Sans Web',
    whiteSpace: 'nowrap'
  }
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '& th': {
    border: '1px solid #202020'
  }
}))

const headCells: readonly HeadCell[] = [
  {
    id: 'id',
    numeric: false,
    disablePadding: false,
    label: SelectAssetsStrings.ASSET_ID,
    sortable: true
  },
  {
    id: 'serialNumber',
    numeric: false,
    disablePadding: false,
    label: SelectAssetsStrings.SERIAL_NUMBER,
    sortable: true
  },
  {
    id: 'assetType',
    numeric: false,
    disablePadding: false,
    label: SelectAssetsStrings.ASSET_TYPE,
    sortable: false
  },
  {
    id: 'state',
    numeric: false,
    disablePadding: false,
    label: SelectAssetsStrings.STATE,
    sortable: false
  },
  {
    id: 'firmware_version',
    numeric: false,
    disablePadding: false,
    label: SelectAssetsStrings.FirmwareVersion,
    sortable: false
  },
  {
    id: 'tag',
    numeric: false,
    disablePadding: false,
    label: SelectAssetsStrings.Tag,
    sortable: false
  }
]

interface ParentProps {
  assetType: Option | null
  selectedAssets: string[]
  setSelectedAssets: React.Dispatch<React.SetStateAction<string[]>>
  unselectedAssets: string[]
  setUnselectedAssets: React.Dispatch<React.SetStateAction<string[]>>
  searchInput: string
  setSearchInput: React.Dispatch<React.SetStateAction<string>>
  selectedOption: CheckboxDropdownMode
  setSelectedOption: React.Dispatch<React.SetStateAction<CheckboxDropdownMode>>
  selectedPages: number[]
  setSelectedPages: React.Dispatch<React.SetStateAction<number[]>>
  setConditions: React.Dispatch<React.SetStateAction<Condition[]>>
  setConjunction: React.Dispatch<React.SetStateAction<'AND' | 'OR' | ''>>
  setIsAdvanceFilterSelected: React.Dispatch<React.SetStateAction<boolean>>
}

const SelectAssetComponent = (props: AllProps): JSX.Element => {
  const [assetList, setAssetList] = React.useState<SelectAssetList[]>([])
  const [showTemplates, setShowTemplates] = React.useState<string[]>([])
  const [page, setPage] = React.useState(1)
  const [sort, setSort] = React.useState<Sort>({ order: 'asc', orderBy: 'id' })
  const rowsPerPage = 10
  const [showAdvancedFilter, setShowAdvancedFilter] = React.useState(false)
  const [conditions, setConditions] = React.useState<AdvFilterParameterConditions[]>([])
  const [conjuction, setConjuction] = React.useState<ConjuctionOptions>('AND')
  const [triggerAdvFilter, setTriggerAdvFilter] = React.useState(false)
  const checkboxDropdownContainerRef = useRef<HTMLDivElement>(null)
  const [fullyUnselectedPages, setFullyUnselectedPages] = React.useState<number[]>([])
  const [pagesWithIndividualSelections, setPagesWithIndividualSelections] = React.useState<number[]>([])
  const totalPages = (props.assetForCreateGroup.assetsForCreateGroup != null)
    ? Math.ceil(props.assetForCreateGroup.assetsForCreateGroup.total_count / rowsPerPage)
    : 0
  const allPages = Array.from({ length: totalPages }, (_, i) => i + 1)
  let partiallySelectedPages = props.selectedOption === 'all'
    ? allPages.filter(p => !fullyUnselectedPages.includes(p))
    : Array.from(new Set([...props.selectedPages, ...pagesWithIndividualSelections]))
  if (props.unselectedAssets.length > 0 && props.selectedOption === 'all') {
    const areCurrentPageAssetsUnselected = assetList.every((asset: SelectAssetList) => props.unselectedAssets.includes(asset.asset_id))
    if (areCurrentPageAssetsUnselected) {
      partiallySelectedPages = partiallySelectedPages.filter((p) => p !== page)
    }
  }

  const handleSelectionModeChange = (prevMode: CheckboxDropdownMode, newMode: CheckboxDropdownMode): void => {
    const currPageIds = assetList.map((asset: SelectAssetList) => asset.asset_id)
    if (prevMode === 'none' && newMode === 'all') {
      props.setSelectedAssets([])
      props.setUnselectedAssets([])
      props.setSelectedPages([])
      setPagesWithIndividualSelections([])
      setFullyUnselectedPages([])
    } else if (prevMode === 'none' && newMode === 'current') {
      if (!props.selectedPages.includes(page)) {
        props.setSelectedPages([...props.selectedPages, page])
      }
      props.setSelectedAssets([...props.selectedAssets, ...currPageIds.filter((id) => !props.selectedAssets.includes(id))])
      props.setUnselectedAssets([])
      setFullyUnselectedPages([])
    } else if (prevMode === 'current' && newMode === 'none') {
      const currPageSelectedIds = currPageIds.filter((id) => props.selectedAssets.includes(id))
      props.setSelectedAssets([...props.selectedAssets.filter((id) => !currPageSelectedIds.includes(id))])
      props.setUnselectedAssets([])
      props.setSelectedPages(props.selectedPages.filter((p) => p !== page))
      setFullyUnselectedPages([])
    } else if (prevMode === 'current' && newMode === 'all') {
      props.setSelectedAssets([])
      props.setUnselectedAssets([])
      props.setSelectedPages([])
      setFullyUnselectedPages([])
    } else if (prevMode === 'all' && newMode === 'none') {
      props.setSelectedAssets([])
      props.setUnselectedAssets([])
      props.setSelectedPages([])
      setPagesWithIndividualSelections([])
      setFullyUnselectedPages([])
    } else {
      props.setSelectedAssets(currPageIds)
      props.setUnselectedAssets([])
      props.setSelectedPages([page])
      setPagesWithIndividualSelections([page])
      setFullyUnselectedPages([])
    }
  }

  useEffect(() => {
    const currentPageAssetIds = assetList.map((asset) => asset.asset_id)
    const hasSelections = currentPageAssetIds.some((id) => props.selectedAssets.includes(id))
    if (hasSelections && !pagesWithIndividualSelections.includes(page)) {
      setPagesWithIndividualSelections([...pagesWithIndividualSelections, page])
    } else if (!hasSelections && !props.selectedPages.includes(page)) {
      setPagesWithIndividualSelections(pagesWithIndividualSelections.filter((p) => p !== page))
    }
  }, [assetList, props.selectedAssets, props.selectedPages, page])

  // istanbul ignore next
  useEffect(() => {
    props.setSelectedAssets([])
    props.setUnselectedAssets([])
    props.setSelectedOption('none')
    props.setSelectedPages([])
    setPagesWithIndividualSelections([])
    setFullyUnselectedPages([])
  }, [sort])

  useEffect(() => {
    const currentPageAssetIds = assetList.map((asset: SelectAssetList) => asset.asset_id)
    const selectedAssetsOnCurrentPage = currentPageAssetIds.filter((id) => props.selectedAssets.includes(id))
    if (props.selectedOption === 'current') {
      if (selectedAssetsOnCurrentPage.length === 0 || selectedAssetsOnCurrentPage.length < currentPageAssetIds.length) {
        props.setSelectedOption('none')
      }
    } else if (props.selectedOption === 'none' && selectedAssetsOnCurrentPage.length === currentPageAssetIds.length && currentPageAssetIds.length > 0) {
      props.setSelectedOption('current')
    } else if (props.selectedOption === 'all' && props.assetForCreateGroup.assetsForCreateGroup?.total_count !== undefined && props.unselectedAssets.length === props.assetForCreateGroup.assetsForCreateGroup?.total_count) {
      props.setSelectedOption('none')
    }
  }, [page, sort, props.selectedAssets, assetList, props.selectedOption, props.unselectedAssets])

  // istanbul ignore next
  const resetFilters = (): void => {
    setConditions([])
    setTriggerAdvFilter(prev => !prev)
  }

  // istanbul ignore next
  const openAdvancedFilter = (): void => {
    setShowAdvancedFilter(true) // Show the modal
  }

  /* istanbul ignore next */
  const handleRowClick = (id: string): void => {
    const currentPageAssetIds = assetList.map((asset: SelectAssetList) => asset.asset_id)
    if (props.selectedOption === 'current') {
      if (props.selectedAssets.includes(id)) {
        props.setSelectedAssets(props.selectedAssets.filter((assetId) => assetId !== id))
      } else {
        props.setSelectedAssets([...props.selectedAssets, id])
      }
    } else if (props.selectedOption === 'all') {
      let newUnselectedAssets: string[] = []
      if (props.unselectedAssets.includes(id)) {
        newUnselectedAssets = props.unselectedAssets.filter((assetId) => assetId !== id)
        props.setUnselectedAssets(newUnselectedAssets)
      } else {
        newUnselectedAssets = [...props.unselectedAssets, id]
        props.setUnselectedAssets(newUnselectedAssets)
      }
      const allUnselected = currentPageAssetIds.every((assetId) => newUnselectedAssets.includes(assetId))
      if (allUnselected) {
        setFullyUnselectedPages((prev) => [...new Set([...prev, page])])
      } else {
        setFullyUnselectedPages((prev) => prev.filter((p) => p !== page))
      }
    } else {
      if (props.selectedAssets.includes(id)) {
        props.setSelectedAssets(props.selectedAssets.filter((assetId) => assetId !== id))
      } else {
        props.setSelectedAssets([...props.selectedAssets, id])
      }
    }
    const selectedAssetsOnCurrentPage = currentPageAssetIds.filter((id) => props.selectedAssets.includes(id))
    if (props.selectedOption === 'none' && selectedAssetsOnCurrentPage.length === currentPageAssetIds.length) {
      props.setSelectedOption('current')
      if (!props.selectedPages.includes(page)) {
        props.setSelectedPages([...props.selectedPages, page])
      }
    }
  }

  // istanbul ignore next
  const handleAdvanceFilter = (page: number): void => {
    props.setSearchInput('')

    const advancedFilterMapping: Condition[] = conditions.map(cond => {
      let rowVal: string | string[] = cond.value
      if (cond.operator === 'Between') {
        if (cond.dataType === 'time') {
          rowVal = [dayjs(cond.value[0]).format('HH:mm:ss'), dayjs(cond.value[1]).format('HH:mm:ss')]
        } else {
          rowVal = [cond.value[0], cond.value[1]]
        }
      } else {
        if (cond.dataType === 'time') {
          rowVal = dayjs(cond.value[0]).format('HH:mm:ss')
        } else {
          rowVal = typeof cond.value === 'object' ? cond.value[0] : cond.value
        }
      }

      return {
        key: cond.parameter,
        operation: cond.operator,
        value: rowVal,
        datatype: cond.dataType
      }
    })

    const selectAllOnAdvanceFilter = advancedFilterMapping.map(({ key, operation, value }) => ({
      key,
      operation,
      value
    }))

    const advancedFilterPostRequest: AdvancedFilterPostRequest = {
      conditions: advancedFilterMapping,
      conjuction
    }

    // Update parent with new values
    props.setConditions(selectAllOnAdvanceFilter)
    props.setConjunction(conjuction)
    props.setIsAdvanceFilterSelected(selectAllOnAdvanceFilter.length > 0)

    props.advancedFilter(
      advancedFilterPostRequest,
      page - 1,
      rowsPerPage,
      null,
      sort.order,
      sort.orderBy,
      'CREATE_ASSET_GROUP_PAGE',
      'asset',
      props.assetType?.label ?? ''
    )
  }

  const debouncedHandleFetchAssetList = _.debounce(handleFetchAssetList, 1000)

  // istanbul ignore next
  function handleFetchAssetList (): void {
    if (conditions.length > 0) {
      handleAdvanceFilter(page)
    } else {
      page >= 1 && props.fetchAssetListForCreateGroup(page - 1, rowsPerPage, sort.order, sort.orderBy, props.assetType?.value ?? '', props.searchInput)
    }
  }

  useEffect(() => {
    debouncedHandleFetchAssetList()
  }, [page, sort])

  useEffect(() => {
    if (page > 1) {
      setPage(1)
    } else {
      debouncedHandleFetchAssetList()
    }
    props.setSelectedAssets([])
    props.setUnselectedAssets([])
    props.setSelectedOption('none')
    props.setSelectedPages([])
    setPagesWithIndividualSelections([])
    setFullyUnselectedPages([])
  }, [props.searchInput, triggerAdvFilter])

  // istanbul ignore next
  const handleRequestSort =
        (event: React.MouseEvent<unknown>, newOrderBy: keyof SelectAssetList): void => {
          const isAsc = sort.orderBy === newOrderBy && sort.order === 'asc'
          const toggledOrder = isAsc ? 'desc' : 'asc'
          setSort({ order: toggledOrder, orderBy: newOrderBy })
        }

  useEffect(() => {
    if (props.assetForCreateGroup.assetsForCreateGroup !== undefined && props.assetForCreateGroup.httpStatus === 200) {
      setAssetList(props.assetForCreateGroup.assetsForCreateGroup.group_details)
    }
  }, [props.assetForCreateGroup.assetsForCreateGroup])

  function getShortenedName (name: string): JSX.Element {
    if (name.length > 15) {
      return <abbr style={{ textDecoration: 'none' }} title={name}>{name.slice(0, 20)}...</abbr>
    }
    return <>{name}</>
  }

  const isSelected = (id: string): boolean => (props.selectedOption === 'current' && props.selectedAssets.includes(id)) || (props.selectedOption === 'all' && !props.unselectedAssets.includes(id)) || (props.selectedOption === 'none' && props.selectedAssets.includes(id))

  const getSelectAssetsWithFailure = (): JSX.Element => {
    // istanbul ignore else
    if (props.assetForCreateGroup.assetsForCreateGroup !== undefined && props.assetForCreateGroup.assetsForCreateGroup.group_details.length > 0 && props.assetForCreateGroup.httpStatus === 200) {
      return (
        <>
          <Box sx={style} className="devices">
            <Paper
              sx={{
                width: '100%',
                mb: 2,
                backgroundColor: '#272727',
                boxShadow: 'unset',
                color: '#F0F0F0',
                marginBottom: '0'
              }}
            >
              <TableContainer className="device-table">
                <Table
                  sx={{ minWidth: 750, color: '#f0f0f0' }}
                  aria-labelledby="tableTitle"
                  size={'small'}
                >
                  <EnhancedTableHead
                    containerRef={checkboxDropdownContainerRef}
                    order={sort.order}
                    orderBy={sort.orderBy}
                    onRequestSort={handleRequestSort}
                    dataList={assetList}
                    selectedOption={props.selectedOption}
                    currPageSelectedAssets={assetList.filter((asset) => props.selectedAssets.includes(asset.asset_id)).map((asset: SelectAssetList) => asset.asset_id)}
                    setSelectedOption={props.setSelectedOption}
                    handleSelectionModeChange={handleSelectionModeChange}
                    currPageAssets={assetList.map((asset: SelectAssetList) => asset.asset_id)}
                  />
                  <TableBody>
                    {assetList.length > 0 &&
                      assetList.map((row: SelectAssetList, index: number) => {
                        const isItemSelected = isSelected(row.asset_id)
                        const labelId = `enhanced-table-checkbox-${row.asset_id}`

                        return (
                          <StyledTableRow
                            hover
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            key={row.asset_id}
                            aria-label="groups-list-row"
                            selected={isItemSelected}
                            sx={{ borderLeft: '2px solid #202020', borderRight: '1px solid #202020' }}
                          >
                            <StyledTableCell padding="checkbox">
                                <Checkbox
                                    sx={{ color: '#909090' }}
                                    checked={isItemSelected}
                                    onChange={() => { handleRowClick(row.asset_id) }}
                                    inputProps={{
                                      'aria-labelledby': labelId,
                                      'aria-label': 'test-checkbox',
                                      id: `select-item-${index}`
                                    }}
                                />
                            </StyledTableCell>
                            <StyledTableCell
                              component="th"
                              id={labelId}
                              scope="row"
                              padding="none"
                              aria-label="group-name"
                              data-testid="group-name"
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '25vw'
                              }}
                            >
                              {row.asset_id}
                            </StyledTableCell>
                            <StyledTableCell>
                              {row.serial_number}
                            </StyledTableCell>
                            <StyledTableCell>
                              {row.asset_type}
                            </StyledTableCell>
                            <StyledTableCell>
                              {row.state}
                            </StyledTableCell>
                            <StyledTableCell>
                              {row.firmware_version}
                            </StyledTableCell>
                            <StyledTableCell>
                              {row.tag.length > 0 ? getShortenedName(row.tag[0]) : ''}
                            </StyledTableCell>
                          </StyledTableRow>
                        )
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              {props.assetForCreateGroup.assetsForCreateGroup.total_count > 10 && (<Pagination selectedPages={partiallySelectedPages} count={props.assetForCreateGroup.assetsForCreateGroup.total_count} page={page} setPage={setPage} rowsPerPage={rowsPerPage} siblingCount={1}
                          boundaryCount={1} />)}
            </Paper>
          </Box>
          {
            showTemplates.length > 0
              ? getTemplateModal(showTemplates, () => { setShowTemplates([]) }, 'Asset Configuration')
              : <></>
          }
        </>
      )
    } else if (props.assetForCreateGroup.isLoading) {
      return (
        <div className='CircularprogressAmi'>
          <CircularProgress />
        </div>
      )
    } else if (props.assetForCreateGroup.httpStatus === 401) {
      return (
              <div className='authError'><AuthError errorMessage="Unauthorized"/></div>
      )
    } else if (props.assetForCreateGroup.httpStatus === 403) {
      return (
          <div className='authError'><AuthError errorMessage="accessForbidden"/></div>
      )
    } else if (props.assetForCreateGroup.httpStatus === 404) {
      return (
          <div className='authError'><AuthError errorMessage="NoDataPresent"/></div>
      )
    } else if (((props.assetForCreateGroup.assetsForCreateGroup === undefined || props.assetForCreateGroup.assetsForCreateGroup.group_details.length === 0) && props.assetForCreateGroup.httpStatus === 200) || (props.assetForCreateGroup.httpStatus === 404 && props.assetForCreateGroup.error === 'No Assets Found')) {
      return (
          <div className='authError'><AuthError errorMessage="NoDataPresent"/></div>
      )
    } else if (props.assetForCreateGroup.httpStatus === 400) {
      return (
        <div className='authError'><AuthError errorMessage="invalidSearchInput"/></div>
      )
    } else {
      return (
          <div className='authError'><AuthError errorMessage="cannotFetch" retry={handleFetchAssetList}/></div>
      )
    }
  }

  return (
    <>
      <div>
        <p id='group-info-title'>{SelectAssetsStrings.SELECT_ASSETS}</p>
        <p style={{
          borderTop: '2px solid #202020',
          borderBottom: '2px solid #202020',
          paddingTop: 10,
          paddingBottom: 10,
          marginBottom: 15,
          color: '#f0f0f0',
          fontSize: '16px'
        }}>{SelectAssetsStrings.Asset_List}
          <span style={{ borderLeft: '1px solid #707070', paddingLeft: 10, marginLeft: 10 }}>
            {props.assetForCreateGroup.assetsForCreateGroup !== undefined ? props.assetForCreateGroup.assetsForCreateGroup.total_count : 0} {SelectAssetsStrings.Assets}
          </span>
        </p>
      </div>
      <div style={{ flexDirection: 'row', display: 'flex' }}>
      <Button
          type='hollow'
          dataTestId='advanced-filter-btn'
          width='200px'
          height='35px'
          onClick={openAdvancedFilter}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ marginLeft: '13px' }}>{SelectAssetsStrings.AdvancedFilter} ({conditions.length})</div>
          <span onClick={(e) => {
            e.stopPropagation()
            resetFilters()
          }}>
            <img src={Clearfilter} alt='clear filter' style={{ marginLeft: '25px', width: '18px', paddingTop: '5px' }} />
            </span>
          </div>
        </Button>
        {
          conditions.length === 0 &&
          <SearchBox sx={{ width: '420px' }}
            immediate={true}
            debounce={true}
            placeholder={SelectAssetsStrings.SearchUsingSerialNumberStateTagFirmware}
            emptyEnterFallback={() => { return undefined }}
            searchVal={props.searchInput} setSearchVal={props.setSearchInput}
            performAction={() => { return undefined }}
          />
        }
      </div>
        {getSelectAssetsWithFailure()}
        { showAdvancedFilter && <AdvancedFilter
          conditions={conditions}
          setConditions={setConditions}
          conjuction={conjuction}
          setConjuction={setConjuction}
          setPageSize={setPage}
          setShowAdvancedFilter= {setShowAdvancedFilter}
          triggerAdvFilter={triggerAdvFilter}
          setTriggerAdvFilter={setTriggerAdvFilter}
          searchKey='asset'
          disableOverflow={true}
        />}
    </>
  )
}

interface DispatchToProps {
  fetchAssetListForCreateGroup: (page: number, size: number, sortdir: string, sortfield: string, assetTypeId: string, searchInput: string) => void
  advancedFilter: (advancedFilterPostRequest: AdvancedFilterPostRequest, page: number, size: number, groupId: string | null, sortdir: string, sortfield: string, searchPage: string, searchKey: string, assetType: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchAssetListForCreateGroup: (page: number, size: number, sortdir: string, sortfield: string, assetTypeId: string, searchInput: string) => dispatch(fetchAssetListForCreateGroup(page, size, sortdir, sortfield, assetTypeId, searchInput)),
  advancedFilter: (advancedFilterPostRequest: AdvancedFilterPostRequest, page: number, size: number, groupId: string | null, sortdir: string, sortfield: string, searchPage: string, searchKey: string, assetType: string) =>
    dispatch(advancedAssignedAssetCreateFilter(advancedFilterPostRequest, page, size, groupId, sortdir, sortfield, searchPage, searchKey, assetType))
})

interface StateToProps {
  assetForCreateGroup: AssetsFoCreateGroupState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  assetForCreateGroup: state.assetForCreateGroup
})

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

type AllProps = ParentProps & PropsFromRedux

export default connector(SelectAssetComponent)
